/**
 * "Team activity" tab of the Team page: who added, invited, changed or
 * removed a teammate, newest first. Owner only. A 403 calls `onForbidden`
 * so the page can hide the tab without an error message.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/pages/audit-log/auditLog.scss';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import {
  listTeamAuditLog,
  type AuditLogMeta,
  type TeamAuditEntry,
} from '../../lib/api/audit/audit-api';
import {
  describeTeamEvent,
  formatTeamEventTime,
  teamEventActor,
  teamEventSubject,
} from './team-activity-helpers';

const PER_PAGE = 25;

interface TeamActivityPanelProps {
  roleLabel: (key?: string | null) => string;
  onForbidden: () => void;
}

const TeamActivityPanel: React.FC<TeamActivityPanelProps> = ({
  roleLabel,
  onForbidden,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState<TeamAuditEntry[]>([]);
  const [meta, setMeta] = useState<AuditLogMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (p: number, isCancelled: () => boolean) => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await listTeamAuditLog({ page: p, per_page: PER_PAGE });
        if (isCancelled()) return;
        setEntries(res.data);
        setMeta(res.meta);
      } catch (err: any) {
        if (isCancelled()) return;
        if (err?.status === 403) {
          onForbidden();
          return;
        }
        setFailed(true);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [onForbidden]
  );

  useEffect(() => {
    let cancelled = false;
    load(page, () => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load, page]);

  const current = meta?.current_page || page;
  const last = Math.max(1, meta?.last_page || 1);

  return (
    <div className='teamPage__section teamActivity'>
      <div className='teamPage__sectionHeader'>
        <div>
          <h2>{t('TeamActivityHeading')}</h2>
          <span className='teamPage__sectionSub'>{t('TeamActivitySub')}</span>
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <LoadingSpinner />
      ) : failed ? (
        <div className='auditLogTable__empty' role='alert'>
          {t('TeamActivityLoadFailed')}
        </div>
      ) : entries.length === 0 ? (
        <div className='auditLogTable__empty'>{t('TeamActivityEmpty')}</div>
      ) : (
        <>
          <div className='auditLogTable'>
            <div className='auditLogTable__head'>
              <div className='auditLogTable__cell'>{t('TeamActivityWhen')}</div>
              <div className='auditLogTable__cell'>{t('TeamActivityWho')}</div>
              <div className='auditLogTable__cell'>{t('TeamActivityWhat')}</div>
              <div className='auditLogTable__cell'>{t('TeamActivityWhom')}</div>
            </div>
            {entries.map((entry) => (
              <div
                className='auditLogTable__row'
                key={entry.id}
                data-testid='team-activity-row'
              >
                <div className='auditLogTable__cell auditLogTable__time'>
                  {formatTeamEventTime(entry.created_at)}
                </div>
                <div className='auditLogTable__cell auditLogTable__actor'>
                  {teamEventActor(entry, t)}
                </div>
                <div className='auditLogTable__cell auditLogTable__action'>
                  {describeTeamEvent(entry, t, roleLabel)}
                </div>
                <div className='auditLogTable__cell'>
                  {teamEventSubject(entry, t)}
                </div>
              </div>
            ))}
          </div>
          {last > 1 && (
            <div className='auditLogPagination'>
              <div className='auditLogPagination__controls'>
                <button
                  type='button'
                  className='auditLogPagination__btn'
                  disabled={loading || current <= 1}
                  onClick={() => setPage(current - 1)}
                >
                  {t('Previous')}
                </button>
                <span className='auditLogPagination__page'>
                  {t('AuditLogPageOf', { current, last })}
                </span>
                <button
                  type='button'
                  className='auditLogPagination__btn'
                  disabled={loading || current >= last}
                  onClick={() => setPage(current + 1)}
                >
                  {t('Next')}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TeamActivityPanel;
